import React from "react";
import {Button, ButtonContainer, ButtonType, Checkbox, FileInput, Form, InlineType, Modal, TextInput} from "@renta-apps/athenaeum-react-components";
import {BaseComponent, IBaseComponent} from "@renta-apps/athenaeum-react-common";
import {FileModel} from "@renta-apps/athenaeum-toolkit";
import RentaTaskConstants from "@/helpers/RentaTaskConstants";
import AddWorkOrderAttachmentsRequest from "@/models/server/requests/AddWorkOrderAttachmentsRequest";
import {AttachmentType} from "@/models/Enums";
import Localizer from "@/localization/Localizer";
import FeatureFlags from "@/helpers/FeatureFlags";
import UnleashHelper from "@/helpers/UnleashHelper";

import styles from "./AddWorkReportAttachmentModal.module.scss";

interface IAddWorkReportAttachmentModalProps {
    id: string;
    workOrderId: string;
    addWorkReportAttachments(sender: IBaseComponent, request: AddWorkOrderAttachmentsRequest): Promise<void>;
}

interface IAddWorkReportAttachmentModalState {
    public: boolean,
    requiresAcknowledgment: boolean,
    comment: string,
    files: FileModel[]
}

export default class AddWorkReportAttachmentModal extends BaseComponent<IAddWorkReportAttachmentModalProps, IAddWorkReportAttachmentModalState> {

    state: IAddWorkReportAttachmentModalState = {
        public: false,
        requiresAcknowledgment: false,
        comment: "",
        files: []
    };

    private readonly _modalRef: React.RefObject<Modal> = React.createRef();
    private readonly _formRef: React.RefObject<Form> = React.createRef();

    private get modal(): Modal {
        return this._modalRef.current!;
    }

    private get readonly(): boolean {
        return (this.state.files == null || this.state.files.length == 0);
    }

    private get isAttachmentAcknowledgmentEnabled(): boolean {
        return UnleashHelper.isEnabled(FeatureFlags.AttachmentAcknowledgment);
    }

    private async onChangePublicAsync(value: boolean) {
        await this.setState({public: value});
    }

    private async onChangeRequiresAcknowledgmentAsync(value: boolean) {
        await this.setState({requiresAcknowledgment: value});
    }

    private async onChangeCommentAsync(value: string) {
        await this.setState({comment: value});
    }

    private async onChangeAttachmentFilesAsync(value: FileModel | FileModel[]): Promise<void> {
        if (value instanceof Array) {
            await this.setState({files: value});
        } else {
            await this.setState({files: [value]});
        }
    }

    private async removeAttachmentHandlerAsync(value: FileModel) {
        const indexToRemove: number | null = this.state.files && this.state.files.indexOf(value);

        if (indexToRemove !== null) {
            const files: FileModel[] = this.state.files || [];
            files.splice(indexToRemove, 1);

            await this.setState({files});
        }
    };

    private async onSubmitAsync(): Promise<void> {
        const request = new AddWorkOrderAttachmentsRequest();
        request.workOrderId = this.props.workOrderId;
        request.public = this.state.public;
        request.requiresAcknowledgment = this.state.requiresAcknowledgment;
        request.comment = this.state.comment;
        request.type = AttachmentType.WorkOrderAttachment;
        request.files = this.state.files;

        await this.props.addWorkReportAttachments(this.modal, request);

        await this.modal.closeAsync();
    }

    private async clearStateAsync(): Promise<void> {
        this.state.public = false;
        this.state.requiresAcknowledgment = false;
        this.state.comment = "";
        this.state.files = [];

        await this.setState(this.state);
    }

    private async onCloseAsync(): Promise<void> {
        await this.clearStateAsync();
    }

    public hasSpinner(): boolean {
        return true;
    }

    public async openAsync(): Promise<void> {
        await this.modal.openAsync();
    }

    public render(): React.ReactNode {

        return (
            <Modal id={this.id}
                   ref={this._modalRef}
                   className={styles.addWorkReportAttachmentModal}
                   title={Localizer.addWorkReportAttachmentModalTitle}
                   onClose={async () => await this.onCloseAsync()}>

                <div className="row">
                    <div className="col">

                        <Form ref={this._formRef} id="addWorkReportAttachment" onSubmit={async () => await this.onSubmitAsync()}>

                            <Checkbox id="public"
                                      label={Localizer.addWorkReportAttachmentModalPublicCheckbox}
                                      value={this.state.public}
                                      inlineType={InlineType.Right}
                                      onChange={async (_, value) => await this.onChangePublicAsync(value)}
                            />

                            {this.isAttachmentAcknowledgmentEnabled && (
                                <Checkbox id="requiresAcknowledgment"
                                          label={Localizer.workOrderDetailsPanelGridAttachmentsRequiresAcknowledgment}
                                          value={this.state.requiresAcknowledgment}
                                          inlineType={InlineType.Right}
                                          onChange={async (_, value) => await this.onChangeRequiresAcknowledgmentAsync(value)}
                                />
                            )}

                            <TextInput id="comment"
                                       label={Localizer.genericCommentLanguageItemName}
                                       value={this.state.comment}
                                       onChange={async (_, value) => await this.onChangeCommentAsync(value)}
                            />

                            <FileInput dropZone required multiple
                                       id={`file${this.id}`}
                                       label={Localizer.formInputFileChooseFiles}
                                       maxTotalSize={RentaTaskConstants.maxFileUploadSizeInBytes}
                                       maxSize={RentaTaskConstants.maxFileUploadSizeInBytes}
                                       fileTypes={RentaTaskConstants.allAttachmentFileTypes}
                                       value={this.state.files}
                                       onChange={(_, value: FileModel | FileModel[]) => this.onChangeAttachmentFilesAsync(value)}
                                       onRemove={async (value: FileModel) => await this.removeAttachmentHandlerAsync(value)}
                            />

                            <ButtonContainer>

                                <Button submit
                                        label={Localizer.genericActionSave}
                                        disabled={this.readonly}
                                        type={ButtonType.Orange}
                                        icon={{name: "far save"}}
                                />

                            </ButtonContainer>

                        </Form>

                    </div>
                </div>

            </Modal>
        );
    }
};